import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import * as actions from './redux/actions';
import * as pluginActions from '../plugin/redux/actions';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import ParentContentCard from '../common/ParentContentCard';
import CustomLoader from '../common/CustomLoader';
import EmailTaskSchedule from './EmailTaskSchedule';
import ExternalApiTaskSchedule from './ExternalApiTaskSchedule';
import CustomInputWithIcon from '../../common/ui/CustomInputWithIcon';
import CustomSelect from '../../common/formElements/CustomSelect';
import { scheduleTypes, taskFrequencies } from '../../common/utils/schedule-utils';
import { MetaDataMappingSchedule } from './MetaDataMappingSchedule';
import SyncGoogleCalendarEventsSchedule from './SyncGoogleCalendarEventsSchedule';
import { isProjectReadOnly } from '../../common/utils/api_utils';

export class UpdateTaskSchedule extends Component {
  static propTypes = {
    schedule: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
  };

  state = {};

  componentDidMount() {
    const { actions, match, pluginActions } = this.props;
    const { taskId, projectId, version } = match.params;
    pluginActions.loadAllInstalledPlugin({ projectId, version });
    actions.loadTaskDetail({ projectId, taskId, version }).then((task) => {
      this.setState({
        name: task.schedule.name,
        taskType: task.schedule.taskType,
        jobStartFrom: task.schedule.jobStartFrom,
        taskFrequency: task.schedule.interval,
      });
    });
  }

  handleSubmit = (values) => {
    const { actions, match, history } = this.props;
    const { taskId, projectId, version } = match.params;
    actions.updateSchedule({ projectId, taskId, values, version }).then(() => {
      const redirectPath = `/schedule/${version}/v/${projectId}/`;
      history.push(redirectPath);
    });
  };

  handleCancel = () => {
    const { match, history } = this.props;
    const { projectId, version } = match.params;
    history.push(`/schedule/${version}/v/${projectId}/`);
  };

  handleUpdate = (values) => {
    const { actions, match } = this.props;
    const { taskId, projectId, version } = match.params;
    actions.changeTaskDetail({ projectId, taskId, values, version });
  };

  updateTaskDetail = (isReadOnly) => {
    const { schedule, match, history, plugin } = this.props;
    const { installedPlugins } = plugin;
    const { taskDetail } = schedule;
    if (!taskDetail) {
      return 'No Task Found';
    }
    const { projectId, version } = match.params;
    const { taskType } = taskDetail;
    if (taskType === 'SEND_EMAIL') {
      return (
        <EmailTaskSchedule
          projectId={projectId}
          handleSubmit={this.handleSubmit}
          taskDetail={taskDetail}
          version={version}
          history={history}
          installedPlugins={installedPlugins}
          isReadOnly={isReadOnly}
        />
      );
    } else if (taskType === 'MAKE_API_CALL') {
      return (
        <ExternalApiTaskSchedule
          projectId={projectId}
          handleSubmit={this.handleSubmit}
          taskDetail={taskDetail}
          version={version}
          history={history}
          isReadOnly={isReadOnly}
        />
      );
    } else if (taskType === 'MAP_ALL_DATA') {
      return (
        <MetaDataMappingSchedule
          projectId={projectId}
          handleSubmit={this.handleSubmit}
          taskDetail={taskDetail}
          version={version}
          history={history}
          isReadOnly={isReadOnly}
        />
      );
    } else if (taskType === 'SYNC_GOOGLE_CALENDAR_EVENTS') {
      return (
        <SyncGoogleCalendarEventsSchedule
          projectId={projectId}
          handleSubmit={this.handleSubmit}
          taskDetail={taskDetail}
          version={version}
          history={history}
          isReadOnly={isReadOnly}
        />
      );
    }
  };

  scheduleTaskValidation = () => {
    const { match, schedule } = this.props;
    const { projectId } = match.params;
    const { taskDetail } = schedule;
    let validationSchema = {
      name: Yup.string()
        .required("Task name can't be blank.")
        .max(100, 'Task name can not be more than 100 characters.')
        .matches(/^[A-Za-z0-9\s]+$/, 'Task name should not contain special characters.'),
    };
    return Yup.object().shape(validationSchema);
  };

  handleCancel = () => {
    const { match, history } = this.props;
    const { projectId, version } = match.params;
    history.push(`/schedule/${version}/v/${projectId}/`);
  };

  renderChangeInTask = (isReadOnly) => {
    const { schedule } = this.props;
    const { taskDetail } = schedule;
    if (!taskDetail) {
      return '';
    }
    const { name, frequency, interval, taskType, jobStartFrom } = taskDetail;
    const schema = {
      name,
      taskType,
      taskFrequency: interval,
      jobStartFrom,
    };
    return (
      <Formik
        enableReinitialize={true}
        initialValues={schema}
        validationSchema={this.scheduleTaskValidation()}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          !isReadOnly && this.handleUpdate(values);
          setSubmitting(false);
          resetForm();
        }}
      >
        {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
          <Form className="form-horizontal">
            <div className="row mx-auto">
              <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                <CustomInputWithIcon icon="text" required={true}>
                  <Field
                    type="text"
                    name="name"
                    className="form-control form-control-sm"
                    placeholder="Task Name"
                  />
                  <p className="text-danger">
                    <ErrorMessage name={'name'} />
                  </p>
                </CustomInputWithIcon>
              </div>
              <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                <CustomInputWithIcon title="Task Type" icon="select" required={false}>
                  <CustomSelect
                    name="taskType"
                    isMulti={false}
                    options={scheduleTypes}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values.taskType}
                    placeholder="Task Type"
                  />
                </CustomInputWithIcon>
              </div>
              <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                <CustomInputWithIcon title="Task Frequency" icon="select" required={false}>
                  <CustomSelect
                    name="taskFrequency"
                    isMulti={false}
                    options={taskFrequencies}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values.taskFrequency}
                    placeholder="Task Frequency"
                  />
                </CustomInputWithIcon>
              </div>
              {/* <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                <CustomInputWithIcon title="Job Start From" icon="number" required={false}>
                  <Field
                    type="number"
                    className="form-control form-control-sm"
                    name="jobStartFrom"
                    placeholder="Job Start From"
                    min={0}
                    step={1}
                    max={24}
                  />
                </CustomInputWithIcon>
              </div> */}
            </div>
            <div className="row">
              <div className="col-md-6  ">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-md"
                  onClick={this.handleCancel}
                >
                  <i className="wb webconnect-times"></i> Cancel
                </button>
              </div>
              <div className="col-md-6 text-right">
                <button
                  type="submit"
                  className="btn btn-theme-submit btn-md"
                  disabled={isSubmitting || isReadOnly}
                  title={isReadOnly && 'Read-Only Mode'}
                >
                  Update
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    );
  };

  render() {
    const { match, schedule } = this.props;
    const { projectId } = match.params;
    const { loadTaskDetailPending, taskDetail } = schedule;
    const isReadOnly = isProjectReadOnly(projectId);
    return (
      <CommonPageComponent>
        {loadTaskDetailPending ? (
          <ParentContentCard icon="alarm" title="Update Task">
            <div className="text-center">
              <CustomLoader loading={loadTaskDetailPending} size="20px" color="#303453" />
            </div>
          </ParentContentCard>
        ) : (
          <>
            <ParentContentCard icon="alarm" title="Background Task Schedule">
              {this.renderChangeInTask(isReadOnly)}
            </ParentContentCard>
            <ParentContentCard icon="alarm" title="Background Task Details">
              {this.updateTaskDetail(isReadOnly)}
            </ParentContentCard>
          </>
        )}
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    schedule: state.schedule,
    plugin: state.plugin,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    pluginActions: bindActionCreators({ ...pluginActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UpdateTaskSchedule);
